import {
  VALIDATION_REGEX_TIME,
  VALIDATION_REGEX_ZIP_CODE,
} from "@/shared/lib/validation";
import { z } from "zod";

const requiredErrorInput = { required_error: "入力してください" } as const;
const requiredErrorSelect = { required_error: "選択してください" } as const;
const negativeNumberError = "0以上の数字を入力してください";

export const createOfferCargoDestinationFormSchema = z.object({
  hasLoading: z.boolean().optional(),
  hasUnloading: z.boolean().optional(),
  deadlineType: z
    .enum(["till", "just", "after", "earliest", "term"], requiredErrorSelect)
    .optional(),
  deadlineTime: z.string().regex(VALIDATION_REGEX_TIME).optional(),
  deadlineTimeEnd: z.string().regex(VALIDATION_REGEX_TIME).optional(),
  zipCode: z
    .string()
    .regex(
      VALIDATION_REGEX_ZIP_CODE,
      "郵便番号は 123-4567 の形式で入力してください",
    )
    .optional(),
  prefecture: z.string(requiredErrorInput),
  city: z.string(requiredErrorInput),
  addressLine: z.string().optional(),
  buildingName: z.string().optional(),
  chargePersonName: z.string().optional(),
  contactInfo: z.string().optional(),
  taskDetail: z
    .enum(
      [
        "forklift_use_owner",
        "forklift_use_driver",
        "handlift",
        "unloading",
        "crane",
        "other",
      ],
      requiredErrorSelect,
    )
    .optional(),
  memo: z.string().max(1000).optional(),
});

export const createOfferCargoFormSchema = z.object({
  loadingOn: z.string(requiredErrorInput).date("日付の形式が正しくありません"),
  unloadingOn: z
    .string(requiredErrorInput)
    .date("日付の形式が正しくありません"),
  destinations: z
    .array(createOfferCargoDestinationFormSchema)
    .min(2, "最低「積地」と「卸地」の2箇所を登録してください"),
  requestProposal: z.boolean().optional(),
  shippingFee: z.number(requiredErrorInput).min(0, negativeNumberError),
  highwayFee: z.number(requiredErrorInput).min(0, negativeNumberError),
  highwaySection: z.string().optional(),
  fuelSurcharge: z.number().min(0, negativeNumberError).optional(),
  cargoHandlingFee: z.number().min(0, negativeNumberError).optional(),
  othersFee: z.number().min(0, negativeNumberError).optional(),
  adjustmentFee: z.number().min(0, negativeNumberError).optional(),
  paymentType: z
    .enum(["vehicle_count", "weight"], requiredErrorSelect)
    .default("vehicle_count"),
  paymentDate: z.string().optional(),
  basicFeeMemo: z.string().optional(),
  name: z.string().optional(),
  packingStyle: z.enum(
    [
      "pallet",
      "case",
      "paper_band",
      "freight_conveyor",
      "none_package",
      "roll_box_pallet",
      "container",
      "drum",
      "plastic_container",
      "other",
    ],
    requiredErrorSelect,
  ),
  count: z.number().min(0, negativeNumberError).optional(),
  weight: z.number().min(0, negativeNumberError).optional(),
  depth: z.number().min(0, negativeNumberError).optional(),
  width: z.number().min(0, negativeNumberError).optional(),
  height: z.number().min(0, negativeNumberError).optional(),
  vehicleShape: z.enum(
    [
      "other",
      "flat",
      "box",
      "hood",
      "dump",
      "semi_trailer",
      "full_trailer",
      "wing",
      "unic",
      "refrigerator",
      "freezer",
    ],
    requiredErrorSelect,
  ),
  vehicleWeight: z.number(requiredErrorSelect),
  vehicleEquipmentMemo: z
    .string()
    .max(1000, "1000文字以内で記入してください")
    .optional(),
  memo: z.string().max(1000, "1000文字以内で記入してください").optional(),
  deadlineAt: z
    .string({
      ...requiredErrorInput,
    })
    .datetime({ message: "日時の形式が正しくありません", offset: true }),
});

export type CreateOfferCargoDestinationFormSchemaType = z.infer<
  typeof createOfferCargoDestinationFormSchema
>;
export type CreateOfferCargoFormSchemaType = z.infer<
  typeof createOfferCargoFormSchema
>;
